import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  HiOutlinePlus,
  HiOutlineChevronRight,
  HiExclamationCircle,
  HiOutlineRefresh,
  HiOutlineCloudDownload,
  HiOutlineExclamation,
  HiOutlineSparkles,
  HiOutlineTrash,
} from "react-icons/hi";
import { PiClockCounterClockwise, PiCheckBold } from "react-icons/pi";
import { MdDragIndicator } from "react-icons/md";
import { LuNetwork } from "react-icons/lu";
import { toast } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import API from "../../api";
import { CONFIG_STATUS, CONFIG_STATUS_LABEL, buildTriggerFilterFields, readListState, writeListState, needsVerification, isShowingDeleted } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import BulkActionBar from "../../components/selection/BulkActionBar";
import { capture } from "../../instrument";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import useSelection from "../../hooks/useSelection";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useReorderable from "../../hooks/useReorderable";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";

const DEFAULT_VIEWS = [
  { _id: "ALL", name: "Toutes", filters: [] },
  { _id: "NEW", name: "Nouvelles", filters: [{ id: "default-new", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["CREE"] }] },
  { _id: "UPDATED", name: "Mises à jour", filters: [{ id: "default-updated", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["MIS_A_JOUR", "CONFLIT"] }] },
  { _id: "MISSING", name: "Disparues", filters: [{ id: "default-missing", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["DELETED"] }] },
];

const STORAGE_KEY = "question-list-state";

const Questions = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");
  const [showDeleted, setShowDeleted] = useState(isShowingDeleted(savedState?.filters));

  const listRef = useRef(null);
  const dragPreviewRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const {
    data: questions = [],
    isLoading: isLoadingQuestions,
    isError: isErrorQuestions,
  } = useQuery({
    queryKey: ["questions", { showDeleted }],
    queryFn: async () => {
      const response = await API.get(`/question?includeDeleted=${showDeleted}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const reorderMutation = useMutation({
    mutationFn: ({ ids, beforeId, afterId }) => API.post("/question/reorder", { ids, beforeId, afterId }),
    onSuccess: (response) => {
      if (!response.ok) {
        toast.error(response.message);
        queryClient.invalidateQueries({ queryKey: ["questions"] });
        return;
      }
      toast.success("Ordre mis à jour");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la mise à jour");
      queryClient.invalidateQueries({ queryKey: ["questions"] });
    },
  });

  const { data: allOverrides = [] } = useQuery({
    queryKey: ["sync-overrides"],
    queryFn: async () => {
      const response = await API.get("/sync/override");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const conflictDocIds = useMemo(() => new Set(allOverrides.filter((o) => o.conflict).map((o) => String(o.docId))), [allOverrides]);
  const overrideDocIds = useMemo(() => new Set(allOverrides.map((o) => String(o.docId))), [allOverrides]);

  const { data: sharingNetwork } = useQuery({
    queryKey: ["network-config"],
    queryFn: async () => {
      const response = await API.get("/network-config");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    staleTime: Infinity,
  });

  const filterFields = useMemo(() => {
    const uniqueAudiences = [...new Set(questions.flatMap((q) => ["secretary", q.patient && "patient", q.ide && "ide"].filter(Boolean)))].sort();
    const uniqueSyncSources = [...new Set(questions.flatMap((q) => q._sources?.map((s) => s.source)).filter(Boolean))].sort();
    const triggerFilters = buildTriggerFilterFields(questions, [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT]);

    return [
      {
        key: "audience",
        label: "Audience",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueAudiences,
        resolve: (question) => ["secretary", question.patient && "patient", question.ide && "ide"].filter(Boolean),
      },
      ...triggerFilters,
      {
        key: "syncStatus",
        label: "Status synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: ["CONFLIT", "CREE", "MIS_A_JOUR", "MODIFIE", "DELETED", "SYNCED"],
        resolve: (question) => {
          const statuses = [];
          if (conflictDocIds.has(String(question._id))) statuses.push("CONFLIT");
          if (question.configStatus === CONFIG_STATUS.CREATED) statuses.push("CREE");
          if (question.configStatus === CONFIG_STATUS.UPDATED) statuses.push("MIS_A_JOUR");
          if (overrideDocIds.has(String(question._id))) statuses.push("MODIFIE");
          if (question._sources?.some((s) => s.missingFromSource)) statuses.push("DELETED");
          if (statuses.length === 0 && question._sources?.length) statuses.push("SYNCED");
          return statuses;
        },
      },
      {
        key: "syncSource",
        label: "Source synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueSyncSources,
        resolve: (question) => question._sources?.map((s) => s.source).filter(Boolean) || [],
      },
      {
        key: "deleted",
        label: "Supprimée",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (question) => (question.isDeleted ? "Oui" : "Non"),
      },
      ...(sharingNetwork
        ? [
            {
              key: "network",
              label: "Réseau",
              operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
              values: ["PROPRIETAIRE", "RECU", "AUCUN"],
              valueLabels: { PROPRIETAIRE: "Partagée (propriétaire)", RECU: "Reçue du réseau", AUCUN: "Non partagée" },
              resolve: (question) => (question.sharedToNetwork ? "PROPRIETAIRE" : question.networkSourceId ? "RECU" : "AUCUN"),
            },
          ]
        : []),
    ];
  }, [questions, conflictDocIds, overrideDocIds, sharingNetwork]);

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    setShowDeleted(isShowingDeleted(nextFilters));
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "question",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const filterCounts = useMemo(() => {
    const created = questions.filter((doc) => doc.configStatus === CONFIG_STATUS.CREATED).length;
    const updated = questions.filter((doc) => doc.configStatus === CONFIG_STATUS.UPDATED || conflictDocIds.has(String(doc._id))).length;
    const missing = questions.filter((doc) => doc._sources?.some((s) => s.missingFromSource)).length;
    return { all: questions.length, created, updated, missing };
  }, [questions, conflictDocIds]);

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.all}</span>;
    if (tab._id === "NEW" && filterCounts.created > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.created}</span>;
    if (tab._id === "UPDATED" && filterCounts.updated > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.updated}</span>;
    if (tab._id === "MISSING" && filterCounts.missing > 0) return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.missing}</span>;
    return null;
  };

  const filteredQuestions = useMemo(() => {
    const result = applyFilters(questions);
    return rankBySearch(result, search, (q) => [q.question, q.questionCode, q.patientQuestion, q.ideQuestion].filter(Boolean).join(" "));
  }, [questions, search, activeFilters, conflictDocIds, overrideDocIds]);

  const orderedIds = useMemo(() => filteredQuestions.map((q) => q._id), [filteredQuestions]);
  const selection = useSelection(orderedIds);
  const selectedNeedsVerification = useMemo(() => questions.some((q) => selection.selected.has(q._id) && needsVerification(q)), [questions, selection.selected]);
  const selectionHasDeleted = useMemo(() => questions.some((q) => selection.selected.has(q._id) && q.isDeleted), [questions, selection.selected]);
  const selectedShareableIds = useMemo(
    () => questions.filter((q) => selection.selected.has(q._id) && !q.isDeleted && !q.sharedToNetwork && !q.networkSourceId).map((q) => q._id),
    [questions, selection.selected],
  );

  const bulkMutation = useMutation({
    mutationFn: ({ action }) => API.post("/question/bulk", { ids: Array.from(selection.selected), action }),
    onSuccess: (response, variables) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      const { processed = 0, skipped = 0 } = response.data || {};
      const past = { delete: "supprimée(s)", verify: "vérifiée(s)" }[variables.action];
      let message = `${processed} question(s) ${past}`;
      if (skipped > 0) message += ` — ${skipped} ignorée(s)`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      selection.clear();
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const shareMutation = useMutation({
    mutationFn: () => API.post("/network-config/question", { fromIds: selectedShareableIds }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      const { processed = 0, skipped = 0, errors = [] } = response.data || {};
      if (processed === 0 && errors.length > 0) return toast.error(errors[0].message);
      let message = `${processed} question(s) partagée(s) au réseau`;
      if (skipped > 0) message += ` — ${skipped} ignorée(s)`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      selection.clear();
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const virtualizer = useVirtualizer({
    count: filteredQuestions.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredQuestions.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredQuestions.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const question = filteredQuestions[selectedIndexRef.current];
        if (!question) return;
        if (newTab) window.open(`/question/${question._id}/edit`, "_blank");
        else navigateToQuestion(question);
      },
    },
    [filteredQuestions, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/question/add"), { preventDefault: true });
  useHotkeys("x", () => selection.select(selectedIndexRef.current), { preventDefault: true, enabled: filteredQuestions.length > 0 }, [filteredQuestions.length]);
  useHotkeys(
    "mod+a",
    (e) => {
      e.preventDefault();
      selection.selectAll();
    },
    { preventDefault: true },
    [orderedIds],
  );
  useHotkeys("escape", () => selection.clear(), { enabled: selection.count > 0 }, [selection.count]);

  const navigateToQuestion = (item) => {
    const ids = filteredQuestions.map((c) => c._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(item._id) });
    history.push({ pathname: `/question/${item._id}/edit`, state: { filteredIds: ids } });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  const isSyncOrdered = (question) => (question._sources || []).some((s) => s.source === "KALISIL");

  const { getDragProps, isDragging, showDropBefore, showDropAfter, draggedIndex } = useReorderable(
    filteredQuestions,
    (newItems, { toIndex }) => {
      const draggedId = newItems[toIndex]._id;
      const selectedSet = selection.count > 1 && selection.isSelected(draggedId) ? selection.selected : new Set([draggedId]);
      const syncOrderedIds = new Set(filteredQuestions.filter((q) => isSyncOrdered(q) || q.networkSourceId).map((q) => q._id));
      const movedSet = new Set([...selectedSet].filter((id) => !syncOrderedIds.has(id)));

      let beforeId = null;
      for (let i = toIndex - 1; i >= 0; i--) {
        if (!movedSet.has(newItems[i]._id)) {
          beforeId = newItems[i]._id;
          break;
        }
      }
      let afterId = null;
      for (let i = toIndex + 1; i < newItems.length; i++) {
        if (!movedSet.has(newItems[i]._id)) {
          afterId = newItems[i]._id;
          break;
        }
      }

      const movedBlock = filteredQuestions.filter((q) => movedSet.has(q._id));
      const remaining = newItems.filter((q) => !movedSet.has(q._id));
      const insertAt = beforeId ? remaining.findIndex((q) => q._id === beforeId) + 1 : 0;
      const reordered = [...remaining.slice(0, insertAt), ...movedBlock, ...remaining.slice(insertAt)];

      queryClient.setQueryData(["questions", { showDeleted }], reordered);
      reorderMutation.mutate({ ids: movedBlock.map((q) => q._id), beforeId, afterId });
    },
    {
      scrollRef: listRef,
      getDragImage: (index) => (selection.count > 1 && selection.isSelected(filteredQuestions[index]?._id) ? dragPreviewRef.current : null),
    },
  );

  const groupDragActive = draggedIndex != null && selection.count > 1 && selection.isSelected(filteredQuestions[draggedIndex]?._id);

  const getAudienceColor = (audience) => {
    const colors = {
      secretary: "bg-purple-50 text-purple-700 border-purple-200",
      patient: "bg-blue-50 text-blue-700 border-blue-200",
      ide: "bg-green-50 text-green-700 border-green-200",
    };
    return colors[audience] || "bg-gray-50 text-gray-700 border-gray-200";
  };

  if (isLoadingQuestions) {
    return <PageLoadingSpinner />;
  }

  if (isErrorQuestions) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des questions</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Questions</h1>
          <ViewTabs
            defaultViews={DEFAULT_VIEWS}
            savedViews={savedViews}
            isViewActive={isViewActive}
            onSelectView={handleSelectView}
            onDeleteView={handleDeleteView}
            getViewBadge={getViewBadge}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            onClick={() => window.open("/audit-log/questions", "_blank", "noopener,noreferrer,width=1000,height=800")}
            title="Journal d'audit"
          >
            <PiClockCounterClockwise className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => {
              history.push("/question/add");
              notifyShortcut("CREATE");
            }}
          >
            <HiOutlinePlus className="h-4 w-4" />
            <span>Nouvelle question</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Rechercher une question..."
          saveOptions={saveOptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {filteredQuestions.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const question = filteredQuestions[virtualRow.index];
                const index = virtualRow.index;
                const isSelected = selection.isSelected(question._id);
                const isMissing = !question.isDeleted && question._sources?.some((s) => s.missingFromSource);
                const isConflict = !question.isDeleted && conflictDocIds.has(String(question._id));
                const hasStateChip = isMissing || isConflict || needsVerification(question);
                const syncOrdered = isSyncOrdered(question) || !!question.networkSourceId;
                const canDrag = activeFilters?.length === 0 && search.length === 0 && !reorderMutation.isPending && !syncOrdered;
                const dragClass = isDragging(index) ? "scale-[0.98] bg-gray-100 opacity-50" : groupDragActive && isSelected ? "opacity-40" : "";

                return (
                  <div
                    key={question._id}
                    className="relative"
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                  >
                    {showDropBefore(index) && <div className="absolute -top-px left-4 right-4 z-10 h-0.5 rounded-full bg-primary-500" />}
                    <div
                      style={{ height: virtualRow.size }}
                      {...getDragProps(index)}
                      draggable={canDrag}
                      data-active={virtualRow.index === selectedIndex || undefined}
                      onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                      data-selected={isSelected || undefined}
                      className={`group flex cursor-pointer items-center border-b border-gray-100 pr-4 transition-all duration-150 hover:bg-gray-50 data-[active=true]:bg-primary-50/50 data-[selected=true]:bg-primary-50 ${question.inError ? "bg-red-50/50" : ""} ${dragClass}`}
                      onClick={() => navigateToQuestion(question)}
                    >
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label="Sélectionner la question"
                        onClick={(e) => {
                          e.stopPropagation();
                          selection.select(index, { shiftKey: e.shiftKey });
                        }}
                        className="group/check flex shrink-0 items-center self-stretch pl-4 pr-2"
                      >
                        <span
                          className={`flex h-[18px] w-[18px] items-center justify-center rounded-md border transition-all ${
                            isSelected ? "border-primary-500 bg-primary-500 text-white shadow-sm" : "border-gray-300 bg-white text-transparent group-hover/check:border-primary-400"
                          } ${selection.count > 0 || isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                        >
                          <PiCheckBold className="h-2.5 w-2.5" />
                        </span>
                      </button>
                      <MdDragIndicator
                        title={syncOrdered ? "Ordre géré par la synchronisation" : undefined}
                        onClick={(e) => e.stopPropagation()}
                        className={`mr-3 flex-shrink-0 text-lg ${canDrag ? "cursor-grab text-gray-400 active:cursor-grabbing" : "cursor-not-allowed text-gray-200"}`}
                      />
                      {["secretary", question.patient && "patient", question.ide && "ide"].filter(Boolean).map((audience) => (
                        <span
                          key={audience}
                          className={`mr-1 inline-flex flex-shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-medium ${getAudienceColor(audience)}`}
                        >
                          {audience === "secretary" ? "Secrétaire" : audience === "patient" ? "Patient" : "IDE"}
                        </span>
                      ))}
                      <span className="min-w-0 truncate text-sm font-medium text-gray-900">{question.question}</span>

                      <div className="ml-auto flex flex-shrink-0 items-center gap-4 pl-4">
                        <div className="flex items-center gap-1.5">
                          {question.isDeleted && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                              <HiOutlineTrash className="h-3 w-3" />
                              Supprimée
                            </span>
                          )}
                          {!question.isDeleted && (question.networkSourceId || question.sharedToNetwork) && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                              <LuNetwork className="h-3 w-3" />
                              Réseau
                            </span>
                          )}
                          {!question.isDeleted && needsVerification(question) && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                              {question.configStatus === CONFIG_STATUS.CREATED ? <HiOutlineSparkles className="h-3 w-3" /> : <HiOutlineRefresh className="h-3 w-3" />}
                              {CONFIG_STATUS_LABEL[question.configStatus]}
                            </span>
                          )}
                          {isConflict && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                              <HiOutlineExclamation className="h-3 w-3" />
                              Conflit
                            </span>
                          )}
                          {isMissing && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                              <HiOutlineTrash className="h-3 w-3" />
                              Supprime
                            </span>
                          )}
                          {!question.isDeleted && !hasStateChip && question._sources?.length > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                              <HiOutlineCloudDownload className="h-3 w-3 text-primary-500" />
                              Sync
                            </span>
                          )}
                        </div>
                        {question.inError && <HiExclamationCircle className="flex-shrink-0 text-red-500" title="Cette question contient une erreur" />}
                        <code className="hidden rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-500 sm:block">{question.questionCode}</code>
                        <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                      </div>
                    </div>
                    {showDropAfter(index, filteredQuestions.length) && <div className="absolute -bottom-px left-4 right-4 z-10 h-0.5 rounded-full bg-primary-500" />}
                  </div>
                );
              })}
            </div>
          )}

          {filteredQuestions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-sm">{search || activeFilters.some((f) => f.values.length > 0) ? "Aucun resultat pour les filtres appliques" : "Aucune question trouvée"}</p>
              {!search && activeFilters.length === 0 && <p className="mt-1 text-xs">Créez-en une en cliquant sur le bouton ci-dessus</p>}
            </div>
          )}
        </div>
      </div>

      {selection.count > 1 && (
        <div ref={dragPreviewRef} className="pointer-events-none fixed -left-[9999px] top-0 z-50 pb-3 pr-3">
          <div className="relative">
            {selection.count > 2 && <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-lg border border-gray-200 bg-white" />}
            <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-lg border border-gray-200 bg-white" />
            <div className="relative flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-lg ring-1 ring-black/5">
              <MdDragIndicator className="text-base text-gray-400" />
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-md bg-primary-500 px-1.5 text-xs font-semibold text-white">{selection.count}</span>
              questions
            </div>
          </div>
        </div>
      )}

      <BulkActionBar
        count={selection.count}
        total={filteredQuestions.length}
        noun="question"
        feminine
        loading={bulkMutation.isPending || shareMutation.isPending}
        onClear={selection.clear}
        actions={[
          {
            key: "share",
            label: "Partager au réseau",
            hidden: !sharingNetwork || selectedShareableIds.length === 0,
            onClick: () => shareMutation.mutate(),
          },
          {
            key: "verify",
            label: "Marquer vérifié",
            variant: "primary",
            hidden: !selectedNeedsVerification || selectionHasDeleted,
            onClick: () => bulkMutation.mutate({ action: "verify" }),
          },
          {
            key: "delete",
            label: "Supprimer",
            variant: "danger",
            hidden: selectionHasDeleted,
            confirm: {
              title: `Supprimer ${selection.count} question(s) ?`,
              body: "Cette action est irréversible.",
              confirmLabel: "Supprimer",
            },
            onClick: () => bulkMutation.mutate({ action: "delete" }),
          },
        ]}
      />

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </div>
  );
};

export default Questions;
